import { useCallback } from 'react';
import type { Transition } from '@atlassian/jira-business-board-workflow-issues/src/types.tsx';
import {
	GROUP_BY_ASSIGNEE,
	GROUP_BY_CATEGORY,
	GROUP_BY_PRIORITY,
	GROUP_BY_STATUS,
} from '@atlassian/jira-business-constants';
import { useCategoryField } from '@atlassian/jira-business-entity-project/src/controllers/category-field/index.tsx';
import { StatusCategoryIds } from '@atlassian/jira-common-constants/src/status-categories';
import { STATUS_TYPE, ASSIGNEE_TYPE, PRIORITY_TYPE } from '@atlassian/jira-platform-field-config';
import {
	NULL_GROUP_KEY,
	ASSIGNEE_ID,
	BOARD_FIELD_CATEGORY,
	BOARD_FIELD_PRIORITY,
	BOARD_FIELD_STATUS,
	BOARD_FIELD_USER,
	CATEGORY_ID,
	PRIORITY_ID,
	STATUS_ID,
} from '../../common/constants';
import type { BoardIssue, BoardIssueFields, Group, Rank, StatusGroup } from '../../common/types';
import { useAddConfettiColumn, matchConfettiColumn } from '../column-confetti';
import { useUpdateSessionId } from '../drag-and-drop/card-drag-drop-context';
import { useCardDragDropAnalytics } from '../drag-and-drop/use-card-drag-drop-analytics';
import { useGroups } from '../groups-context';
import { useUpdateIssue } from '../update-issue';

const isIssueNewlyMarkedAsDone = (sourceGroup: Group, destinationGroup: StatusGroup) => {
	if (sourceGroup.type !== GROUP_BY_STATUS) {
		return false;
	}

	return (
		sourceGroup.statusCategoryId !== StatusCategoryIds.done &&
		destinationGroup.statusCategoryId === StatusCategoryIds.done
	);
};

type MoveIssueProps = {
	issue: BoardIssue;
	sourceColumnId: string;
	destinationColumnId: string;
	rank?: Rank;
	transition?: Transition | null;
};

export const useMoveIssue = () => {
	const groups = useGroups();
	const addConfettiColumn = useAddConfettiColumn();
	const { data: categoryField } = useCategoryField();
	const updateIssue = useUpdateIssue();
	const { fireCardDropAnalytics } = useCardDragDropAnalytics();
	const updateSessionId = useUpdateSessionId();

	return useCallback(
		async ({ issue, sourceColumnId, destinationColumnId, rank, transition }: MoveIssueProps) => {
			let showConfetti = false;
			const sourceGroup = groups.find((group) => group.id === sourceColumnId);
			const destinationGroup = groups.find((group) => group.id === destinationColumnId);

			if (sourceGroup == null || destinationGroup == null) {
				throw new Error('Source or destination group not found');
			}

			const destinationGroupType = destinationGroup.type;
			const fieldUpdates: Partial<BoardIssueFields> = {};

			if (sourceColumnId !== destinationColumnId) {
				switch (destinationGroupType) {
					case GROUP_BY_ASSIGNEE:
						fieldUpdates[ASSIGNEE_ID] =
							destinationGroup.id === NULL_GROUP_KEY
								? undefined
								: {
										fieldId: ASSIGNEE_TYPE,
										type: BOARD_FIELD_USER,
										value: destinationGroup.id,
										user: {
											accountId: destinationGroup.id,
											avatarURL: destinationGroup.imageUrl,
											name: destinationGroup.name,
										},
									};
						break;

					case GROUP_BY_CATEGORY:
						if (categoryField) {
							fieldUpdates[CATEGORY_ID] =
								destinationGroup.id === NULL_GROUP_KEY
									? undefined
									: {
											name: categoryField.name,
											fieldId: categoryField.id,
											type: BOARD_FIELD_CATEGORY,
											value: destinationGroup.id,
											category: {
												color: destinationGroup.color,
												name: destinationGroup.name,
											},
										};
						}
						break;

					case GROUP_BY_PRIORITY:
						fieldUpdates[PRIORITY_ID] =
							destinationGroup.id === NULL_GROUP_KEY
								? undefined
								: {
										fieldId: PRIORITY_TYPE,
										type: BOARD_FIELD_PRIORITY,
										value: destinationGroup.id,
										priority: {
											id: destinationGroup.id,
											iconUrl: destinationGroup.imageUrl ?? '',
											name: destinationGroup.name,
										},
									};
						break;

					case GROUP_BY_STATUS: {
						showConfetti = isIssueNewlyMarkedAsDone(sourceGroup, destinationGroup);

						fieldUpdates[STATUS_ID] = {
							fieldId: STATUS_TYPE,
							type: BOARD_FIELD_STATUS,
							value: destinationGroup.id,
							status: {
								id: destinationGroup.id,
								statusCategoryId: destinationGroup.statusCategoryId,
								name: destinationGroup.name,
							},
						};

						break;
					}

					default: {
						const exhaustiveCheck = destinationGroupType;
						throw new Error(`Unexpected destination group type: ${exhaustiveCheck}`);
					}
				}

				// Check if the issue is already moving to a column that should show confetti
				showConfetti = showConfetti || matchConfettiColumn(destinationGroup.name);
			}

			try {
				await updateIssue({
					issue,
					fields: fieldUpdates,
					transition,
					rank,
				});

				if (showConfetti) {
					addConfettiColumn(destinationColumnId);
				}

				if (updateSessionId == null) {
					throw new Error('updateSessionId is required for card drop analytics');
				}

				fireCardDropAnalytics({
					issue,
					sourceGroup,
					destinationGroup,
					rank,
					transition,
					updateSessionId,
				});
			} catch (error: unknown) {
				// errors handled in updateIssue controller
			}
		},
		[addConfettiColumn, categoryField, fireCardDropAnalytics, groups, updateIssue, updateSessionId],
	);
};
